import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { usePeriodicVersionCheck } from "@/hooks/usePeriodicVersionCheck";
import {
  getCurrentVersion,
  reloadForUpdate,
  shouldUpdateVersion,
  updateStoredVersion,
} from "@/utils/versionCheck";

export const VersionUpdateNotification: FC = () => {
  const { t } = useTranslation();
  const [showNotification, setShowNotification] = useState(false);

  const [dismissed, _setDismissed] = useState(false);

  // Periodic server version checks
  usePeriodicVersionCheck((hasUpdate) => {
    if (hasUpdate && !dismissed) {
      setShowNotification(true);
    }
  });

  useEffect(() => {
    // Initial version check on mount
    const hasNewVersion = shouldUpdateVersion();
    if (hasNewVersion && !dismissed) {
      setShowNotification(true);
    }
  }, [dismissed]);

  const handleUpdate = () => {
    updateStoredVersion();
    reloadForUpdate();
  };

  if (!showNotification) {
    return null;
  }

  return (
    <div
      className="animation-slideDown fixed left-0 right-0 top-0 z-[9999] bg-primary-25 shadow-lg"
      data-testid="version-update-notification"
      role="alert"
    >
      <div className="mx-auto flex max-w-[1200px] flex-col items-start justify-between gap-4 p-4 md:flex-row md:items-center md:gap-6 md:p-6">
        {/* Message */}
        <div className="flex-1">
          <strong className="mb-1 block text-base">
            {t("VersionUpdate.title")}
          </strong>
          <p className="my-1 text-base">{t("VersionUpdate.message")}</p>
          <p className="text-sm">
            {t("VersionUpdate.newVersion")}: {getCurrentVersion()}
          </p>
        </div>
        {/* Actions */}
        <div className="flex w-full flex-shrink-0 flex-col gap-3 md:w-auto md:flex-row">
          <Button variant="primary" onClick={handleUpdate}>
            {t("VersionUpdate.reload")}
          </Button>
        </div>
      </div>
    </div>
  );
};
